import styles from '../../../MagicUpload/components/FilteredHeader/FilteredHeader.module.scss';
import localStyles from './SingleSelectSearchDropdown.module.scss';
import { useEffect, useRef, useState } from 'react';
import { useOutsideClick } from '../../../../utils/constants/hooks';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import CaretIcon from '../../../../assets/Svg/CaretIcon';

type SingleSelectSearchDropdownProps = {
    options: { label: string; value: string; endLabel?: string }[];
    selectedItem: { label: string; value: string; endLabel?: string } | null;
    onSelect: (option: { label: string; value: string; endLabel?: string } | null) => void;
    placeholder?: string;
    wrapperStyle?: React.CSSProperties;
    openAbove?: boolean;
    disabled?: boolean;
    loading?: boolean;
    selectedItemStyle?: React.CSSProperties;
    showFloatingLabel?: boolean;
    searchBarStyle?: React.CSSProperties;
    placeholderStyle?: React.CSSProperties;
    clearable?: boolean;
};

const SingleSelectSearchDropdown = (props: SingleSelectSearchDropdownProps) => {
    const {
        options,
        selectedItem,
        onSelect,
        placeholder = 'Select an option',
        wrapperStyle,
        openAbove = false,
        disabled = false,
        loading = false,
        selectedItemStyle,
        showFloatingLabel = true,
        searchBarStyle = {},
        placeholderStyle = {},
        clearable = true,
    } = props;

    const [showFilters, setShowFilters] = useState(false);
    const [searchText, setSearchText] = useState('');
    const dropdownItemRef = useRef<HTMLDivElement>(null);
    const [filteredDropdownOptions, setFilteredDropdownOptions] = useState<
        | {
              label: string;
              value: string;
              endLabel?: string;
          }[]
        | null
    >(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useOutsideClick(dropdownRef, () => {
        setShowFilters(false);
        setSearchText('');
    });

    const handleSelection = (option: { label: string; value: string; endLabel?: string }) => {
        onSelect(option);
        setShowFilters(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(null);
    };

    useEffect(() => {
        if (searchText) {
            setFilteredDropdownOptions(options.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase())));
        } else {
            setFilteredDropdownOptions(options);
        }
    }, [searchText, options]);

    useEffect(() => {
        if (showFilters) {
            setFilteredDropdownOptions(options);
            if (dropdownItemRef.current) {
                dropdownItemRef.current.scrollTo({ behavior: 'smooth', top: 0 });
            }
        }
    }, [showFilters, options]);

    // No need to find selectedOption as we now have the full object

    return (
        <div
            ref={dropdownRef}
            style={{ ...wrapperStyle, ...searchBarStyle }}
            onClick={() => {
                if (!loading && !disabled) {
                    setShowFilters(!showFilters);
                    setSearchText('');
                }
            }}
            className={`${styles.SearchBar} ${localStyles.searchBar} ${selectedItem ? localStyles.searchBarSelected : ''}`}
        >
            {selectedItem ? (
                <>
                    <div className={localStyles.selectedContainer}>
                        {showFloatingLabel && <span className={styles.FloatingLabel}>{placeholder}</span>}
                        <span className={`${styles.SelectedItem} ${localStyles.selectedItemLabel}`} style={{ ...selectedItemStyle }}>
                            {selectedItem.label}
                        </span>
                    </div>
                    <div className={localStyles.endActions}>
                        {clearable ? (
                            <span className={`${(styles as any).ClearSelection || ''} ${localStyles.clearSelection}`} onClick={handleClear}>
                                ✕
                            </span>
                        ) : (
                            <span className={`${styles.DownArrow} ${showFilters ? localStyles.caretRotated : localStyles.caretStatic}`}>
                                <div className={localStyles.caretIconWrapper}>
                                    <CaretIcon colour="#666" width="12" height="12" />
                                </div>
                            </span>
                        )}
                    </div>
                </>
            ) : (
                <>
                    {loading ? (
                        <div className={localStyles.loadingContainer}>
                            <LoadingSpinner color="#6021b3" height="16px" />
                        </div>
                    ) : (
                        <span
                            className={disabled ? localStyles.placeholderDisabled : styles.PlaceholderSingleSelectText}
                            style={placeholderStyle}
                        >
                            {placeholder}
                        </span>
                    )}
                    {!loading && (
                        <span className={showFilters ? localStyles.caretRotated : ''}>
                            <div className={`${localStyles.caretIconWrapper} ${localStyles.caretStatic}`}>
                                <CaretIcon colour="#666" width="12" height="12" />
                            </div>
                        </span>
                    )}
                </>
            )}

            <div
                ref={dropdownItemRef}
                className={`${styles.FilteredDropdownContainer} ${openAbove ? styles.OpenAbove : ''} ${
                    showFilters ? localStyles.dropdownContainerOpen : localStyles.dropdownContainerClosed
                }`}
            >
                <div className={`${styles.DropdownOptionsWrapper} ${localStyles.dropdownOptionsWrapperFlex}`}>
                    {/* Render search at top when not openAbove */}
                    {!openAbove && (
                        <input
                            type="text"
                            placeholder="Search"
                            className={styles.SearchInputFilter}
                            value={searchText}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    )}

                    <div className={`${styles.FilteredDropdownWrapper} ${localStyles.optionsScroll}`}>
                        {filteredDropdownOptions?.length && filteredDropdownOptions?.length > 0 ? (
                            filteredDropdownOptions?.map((option, i) => (
                                <div
                                    key={`${option.label}-${i}`}
                                    className={`${styles.FilteredDropdownItem} ${localStyles.itemJustified} ${
                                        selectedItem && option.value === selectedItem.value ? styles.SelectedDropdownItem : ''
                                    }`}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleSelection(option);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <label className={styles.DropdownLabel} title={option.label}>
                                        {option.label}
                                    </label>
                                    {option.endLabel && (
                                        <span className={localStyles.endLabel} title={option.endLabel}>
                                            {option.endLabel}
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className={styles.NoData}>
                                <span>No Results</span>
                            </div>
                        )}
                    </div>

                    {/* Render search at bottom when openAbove is true */}
                    {openAbove && (
                        <input
                            type="text"
                            placeholder="Search"
                            className={`${styles.SearchInputFilter} ${styles.SearchBottom}`}
                            value={searchText}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleSelectSearchDropdown;
